/**
 * Auth tokens must be scoped per tenant Host (django-tenants).
 * platform.localhost and kasi.platform.localhost are separate sessions/schemas.
 */

import { isSaasSignupHost, isTenantWorkspaceHost } from '@/utils/saasPlatform'

const LEGACY_SESSION_KEY = 'session_key'
const LEGACY_CSRF_KEY = 'csrfToken'

export function getAuthHost() {
  if (typeof window === 'undefined') return 'default'
  return String(window.location.hostname || 'default').toLowerCase()
}

function scopedKey(base, host = getAuthHost()) {
  return `${base}:${host}`
}

function isPlatformAuthHost(host = getAuthHost()) {
  return isSaasSignupHost(host) || host === 'localhost' || host === '127.0.0.1'
}

export function getHostSessionKey() {
  const host = getAuthHost()
  const scoped = localStorage.getItem(scopedKey(LEGACY_SESSION_KEY, host))
  if (scoped) return scoped
  // Adopt the pre-scoping key on the Clinch platform host only
  // (clinchsa.co.za / www / staging / localhost). Tenant subdomains must not
  // reuse a leftover platform session if storage were ever shared.
  const legacy = localStorage.getItem(LEGACY_SESSION_KEY)
  if (legacy && isPlatformAuthHost(host) && !isTenantWorkspaceHost(host)) {
    localStorage.setItem(scopedKey(LEGACY_SESSION_KEY, host), legacy)
    return legacy
  }
  return null
}

export function setHostSessionKey(value) {
  const key = scopedKey(LEGACY_SESSION_KEY)
  if (value) {
    localStorage.setItem(key, value)
    // Drop global legacy key on tenant hosts so old platform sessions are not reused.
    if (isTenantWorkspaceHost(getAuthHost())) {
      localStorage.removeItem(LEGACY_SESSION_KEY)
    }
  } else {
    localStorage.removeItem(key)
  }
}

export function clearHostSessionKey() {
  setHostSessionKey(null)
}

/**
 * Call ``onChange`` when another browser tab on this Host logs in, logs out, or switches user.
 * The ``storage`` event never fires in the tab that made the change.
 */
export function watchHostSessionKey(onChange) {
  if (typeof window === 'undefined') return () => {}
  const key = scopedKey(LEGACY_SESSION_KEY)
  const handler = (event) => {
    if (event.storageArea !== localStorage) return
    if (event.key !== key && event.key !== null) return
    if (event.oldValue === event.newValue) return
    onChange(event.newValue)
  }
  window.addEventListener('storage', handler)
  return () => window.removeEventListener('storage', handler)
}

export function getHostCsrfToken() {
  const host = getAuthHost()
  return (
    localStorage.getItem(scopedKey(LEGACY_CSRF_KEY, host)) ||
    sessionStorage.getItem(scopedKey(LEGACY_CSRF_KEY, host)) ||
    null
  )
}

export function setHostCsrfToken(value) {
  const key = scopedKey(LEGACY_CSRF_KEY)
  if (value) {
    localStorage.setItem(key, value)
    sessionStorage.setItem(key, value)
  } else {
    localStorage.removeItem(key)
    sessionStorage.removeItem(key)
  }
}

export function clearHostCsrfToken() {
  setHostCsrfToken(null)
}
